import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  FormFeedback,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Select from "react-select";
import ItemService from "../../../services/OrderingServices/ItemService";
import UnitService from "../../../services/OrderingServices/UnitService";
import { useNavigate, useParams } from "react-router-dom";
import RecipeService from "../../../services/OrderingServices/RecipeService";
import toast from "react-hot-toast";

const RecipeForm = () => {
  const { id } = useParams();
  const [open, setOpen] = useState("0");
  const [unitList, setUnitList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productData, setProductData] = useState([]);

  const navigate = useNavigate();
  const toggle = (id) => {
    setOpen(open === id ? "" : id);
  };

  const [initialData, setInitialData] = useState([]);
  const fetchRecipeDetail = async () => {
    try {
      const response = await RecipeService.getDetail(id);
      console.log("🚀 ~ fetchRecipeDetail ~ response:", response);
      setInitialData(response);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchRecipeDetail();
  }, [id]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: initialData?.id || 0,
      productName: initialData?.productName || "",
      productId: initialData?.productId || 0,
      description: initialData?.description || "",
      recipeDetails: initialData?.recipeDetails || [
        {
          id: 0,
          ingredientId: 0,
          ingredientName: "",
          quantity: 0,
          unitId: 0,
          description: "",
        },
      ],
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        if (id) {
          await RecipeService.update(id, values);
          navigate("/menu-item");
          toast.success("Recipe updated successfully!");
        } else {
          await RecipeService.create(values);
          resetForm();
          navigate("/receipe-management");
          toast.success("Recipe created successfully!");
        }
      } catch (error) {
        console.error("Submit Error:", error);
        toast.error("Failed to save recipe. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleAddIngredient = () => {
    formik.setFieldValue("recipeDetails", [
      ...formik.values.recipeDetails,
      {
        id: 0,
        ingredientId: 0,
        ingredientName: "",
        price: 0,
        quantity: 0,
        unitId: 0,
        description: "",
      },
    ]);
  };

  const handleRemoveIngredient = (index) => {
    const updated = [...formik.values.recipeDetails];
    updated.splice(index, 1);
    formik.setFieldValue("recipeDetails", updated);
  };

  const breadcrumbItems = [
    { title: "Back To List", link: "/receipe-management" },
  ];

  const fetchProduct = async () => {
    try {
      const response = await ItemService.itemList();
      setProductData(response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUnit = async () => {
    try {
      setLoading(true);
      const response = await UnitService.getList();
      setUnitList(response);
    } catch (error) {
      console.log("🚀 ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
    fetchUnit();
  }, []);

  const productOptions =
    productData?.items?.map((type) => ({
      value: type.id,
      label: type.name,
      price: type.price,
      description: type.description,
    })) || [];

  const unitOptions =
    unitList?.map((type) => ({
      value: type.id,
      label: type.name,
    })) || [];

  const handleProductChange = (selectedOption) => {
    formik.setFieldValue(
      "productId",
      selectedOption ? selectedOption.value : 0
    );
    formik.setFieldValue(
      "productName",
      selectedOption ? selectedOption.label : ""
    );
    formik.setFieldValue("price", selectedOption ? selectedOption.price : 0);
    formik.setFieldValue(
      "description",
      selectedOption ? selectedOption.description : ""
    );
  };

  const handleIngredientChange = (selectedOption, index) => {
    formik.setFieldValue(
      `recipeDetails[${index}].ingredientId`,
      selectedOption ? selectedOption.value : 0
    );
    formik.setFieldValue(
      `recipeDetails[${index}].ingredientName`,
      selectedOption ? selectedOption.label : ""
    );
    formik.setFieldValue(
      `recipeDetails[${index}].price`,
      selectedOption ? selectedOption.price : 0
    );
    formik.setFieldValue(
      `recipeDetails[${index}].quantity`,
      selectedOption ? 1 : 0
    );
    formik.setFieldValue(
      `recipeDetails[${index}].description`,
      selectedOption ? selectedOption.description : ""
    );
  };

  const handleUnitChange = (selectedOption, index) => {
    formik.setFieldValue(
      `recipeDetails[${index}].unitId`,
      selectedOption ? selectedOption.value : 0
    );
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          pageTitle="Recipe"
          title="Recipe Form"
          breadcrumbItems={breadcrumbItems}
        />
      </div>

      <Form onSubmit={formik.handleSubmit}>
        {/* Product Details */}
        <div
          className="d-flex justify-content-end align-content-center mb-3 gap-3 bg-light py-3"
          style={{ position: "sticky", top: "8vh", zIndex: 1 }}
        >
          <Button
            color="danger"
            type="button"
            onClick={() => navigate("/receipe-management")}
          >
            Cancel
          </Button>
          <Button color="success" type="submit">
            Save Recipe
          </Button>
        </div>
        <Row>
          <Col md={4}>
            <Card>
              <CardBody>
                <div className="mb-3">
                  <Label>Product</Label>
                  <Select
                    id="productId"
                    name="productId"
                    options={productOptions}
                    value={
                      productOptions.find(
                        (option) => option.value === formik.values.productId
                      ) || null
                    }
                    onChange={handleProductChange}
                    placeholder="Select Product"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (baseStyles) => ({
                        ...baseStyles,
                      }),
                    }}
                  />
                </div>
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    rows={8}
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                  />
                </FormGroup>
              </CardBody>
            </Card>
          </Col>

          <Col md={8}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5>Recipe Ingredients</h5>
                  <Button
                    color="success"
                    size="sm"
                    type="button"
                    onClick={handleAddIngredient}
                  >
                    <i className="bx bx-plus"></i> Add Ingredient
                  </Button>
                </div>

                {/* ✅ Fixed Accordion Section */}
                <Accordion open={open} toggle={toggle}>
                  {formik.values.recipeDetails.length > 0 ? (
                    formik.values.recipeDetails.map((item, index) => (
                      <AccordionItem key={index}>
                        <AccordionHeader targetId={index.toString()}>
                          <div className="d-flex justify-content-between w-100 align-items-center">
                            <h5>
                              {item.ingredientName ||
                                `Ingredient #${index + 1}`}
                            </h5>
                            <Button
                              color="light"
                              size="sm"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveIngredient(index);
                              }}
                            >
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          </div>
                        </AccordionHeader>
                        <AccordionBody accordionId={index.toString()}>
                          <Row className="">
                            <Col md={4} className="mb-2">
                              <div>
                                <Select
                                  id={`ingredientId-${index}`}
                                  name={`recipeDetails[${index}].ingredientId`}
                                  options={productOptions}
                                  value={
                                    productOptions.find(
                                      (option) =>
                                        option.value === item.ingredientId
                                    ) || null
                                  }
                                  onChange={(selectedOption) =>
                                    handleIngredientChange(
                                      selectedOption,
                                      index
                                    )
                                  }
                                  placeholder="Ingredient"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                    control: (baseStyles) => ({
                                      ...baseStyles,
                                    }),
                                  }}
                                />
                              </div>
                            </Col>

                            <Col md={4} className="mb-2">
                              <Input
                                name={`recipeDetails[${index}].quantity`}
                                type="number"
                                placeholder="Enter the quantity"
                                value={item.quantity}
                                onChange={formik.handleChange}
                              />
                            </Col>
                            <Col md={4} className="mb-2">
                              <div className="">
                                <Select
                                  id={`unitId-${index}`}
                                  name={`recipeDetails[${index}].unitId`}
                                  options={unitOptions}
                                  value={
                                    unitOptions.find(
                                      (option) =>
                                        option.value === item.unitId
                                    ) || null
                                  }
                                  onChange={(selectedOption) =>
                                    handleUnitChange(selectedOption, index)
                                  }
                                  placeholder="Unit"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                    control: (baseStyles) => ({
                                      ...baseStyles,
                                    }),
                                  }}
                                />
                              </div>
                            </Col>
                            <Col md={12}>
                              <FormGroup>
                                <Input
                                  name={`recipeDetails[${index}].description`}
                                  type="textarea"
                                  value={item.description}
                                  placeholder="Enter the description"
                                  onChange={formik.handleChange}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        </AccordionBody>
                      </AccordionItem>
                    ))
                  ) : (
                    <div className="text-center p-3">
                      Please add an ingredient to view them here.
                    </div>
                  )}
                </Accordion>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default RecipeForm;
